/**
 * `lazyRoute` — `React.lazy` qui survit à un graphe de modules périmé.
 *
 * ── Le défaut qu'il corrige ───────────────────────────────────────────────────
 * Une page de route est chargée par import dynamique. L'URL de ce module est
 * figée dans le bundle au moment où l'onglet a chargé l'application : une
 * empreinte de chunk en production (`/assets/XxxPage-a1b2c3.js`), un horodatage
 * HMR en développement (`/src/pages/XxxPage.tsx?t=…`).
 *
 * Si l'application est redéployée — ou le serveur de développement redémarré —
 * pendant que l'onglet est resté ouvert, cette URL n'existe plus. L'import part
 * en **404**, et l'écran tombe sur « Une erreur inattendue est survenue ».
 *
 * ⚠️ Le piège : **React MÉMORISE le rejet d'un `lazy`.** Le bouton « Réessayer »
 * de la frontière d'erreur re-rend le même composant paresseux, dont la promesse
 * est déjà rejetée — il échoue donc identiquement, pour toujours. Le HMR n'y
 * change rien non plus : il met à jour les modules vivants, pas celui qui n'a
 * jamais pu se charger. Seul un rechargement complet répare, et l'utilisateur n'a
 * aucune raison de le deviner.
 *
 * En production, ce scénario est la règle, pas l'exception : tout utilisateur
 * dont l'onglet est ouvert au moment d'un déploiement le rencontre à sa
 * prochaine navigation vers une page qu'il n'avait pas encore visitée.
 *
 * ── Le remède ─────────────────────────────────────────────────────────────────
 * Recharger la page UNE fois, automatiquement. Le drapeau de session empêche la
 * boucle : si l'échec persiste après rechargement, c'est une vraie panne (réseau
 * coupé, fichier réellement absent) et l'erreur remonte normalement à la
 * frontière, avec son message.
 */

import { lazy, type ComponentType } from 'react'

// `ComponentType<any>` est ce que `React.lazy` attend : le resserrer ferait
// échouer l'affectation pour toute page prenant des props.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>

const RELOAD_FLAG = 'argalis:stale-chunk-reload'

type ModuleFactory<T> = () => Promise<{ default: T }>

export function lazyRoute<T extends AnyComponent>(factory: ModuleFactory<T>) {
    return lazy(async () => {
        try {
            const mod = await factory()
            // Un import réussi prouve que le graphe est de nouveau sain : on
            // rearme le filet pour le PROCHAIN déploiement.
            try {
                sessionStorage.removeItem(RELOAD_FLAG)
            } catch {
                /* stockage indisponible (navigation privée stricte) : sans importance */
            }
            return mod
        } catch (err) {
            let alreadyReloaded = true
            try {
                alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) !== null
                if (!alreadyReloaded) sessionStorage.setItem(RELOAD_FLAG, String(Date.now()))
            } catch {
                // Sans sessionStorage, on ne peut pas garantir l'absence de
                // boucle : on préfère laisser l'erreur remonter.
                alreadyReloaded = true
            }
            if (!alreadyReloaded) {
                window.location.reload()
                // La promesse ne se résout JAMAIS : le rechargement est en cours,
                // rien ne doit être rendu entre-temps (surtout pas la frontière
                // d'erreur, qui afficherait un message pour rien).
                return new Promise<{ default: T }>(() => {})
            }
            throw err
        }
    })
}

export default lazyRoute
